import React, { useState } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import { List } from "react-native-paper";
import { attributes } from "../../constants/maps";
import trailsData from "../../assets/data/trails.json";
import { COLORS, descriptionIcons, difficultyStyles, labelDescriptions, defaultfilterValues } from "../../constants";
import { faChevronUp, faChevronDown, faHiking, faSquareCheck } from "@fortawesome/free-solid-svg-icons";
import { setData, getData } from "../../utils/storage";
import { toTitleCase, getDistinctValues } from "../../utils/utility";
import { useNavigation } from "@react-navigation/native";

/**
 * Custom hook to manage the filter view state and user interactions.
 * @returns {object} An object containing the filter attributes, selected values, and functions to handle user interactions.
 */
export const useFilterView = () => {
  const navigation = useNavigation();
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const [selectedValues, setSelectedValues] = useState({});

  /**
   * Toggles the expanded state of an attribute.
   * @param {string} attribute - The attribute to expand or collapse.
   * @returns {void}
   */
  const handlePress = (attribute) => {
    setExpandedAttributes((prev) => ({
      ...prev,
      [attribute]: !prev[attribute],
    }));
  };

  /**
   * Clears all selected filter values.
   */
  const handleReset = async () => {
    setSelectedValues({});
    setData("filterValues", JSON.stringify(defaultfilterValues));
    const storedValues = JSON.parse(await getData("filterValues"));
    if (storedValues) {
      setSelectedValues(storedValues);
    }
  };

  /**
   * Placeholder function to handle applying filter results.
   * Currently logs a message to the console.
   */
  const handleFilterResults = () => {
    let filterValues = selectedValues;
    // Sets filter to default values if absolutely no filters are selected
    if (Object.keys(selectedValues).length === 1 && selectedValues.facility_type.length === 0) {
      filterValues = defaultfilterValues;
    }
    setData("filterValues", JSON.stringify(filterValues));
    setData("showSnackbar", "true");
    navigation.goBack(null);
  };

  /**
   * Handles the selection of a filter item.
   * @param {string} attribute - The attribute to filter by.
   * @param {string} value - The value to filter by.
   * @returns {void}
   */
  const handleItemPress = (attribute, value) => {
    console.log("Attribute: ", attribute, "Value: ", value); // FOR TESTING PURPOSE: To be removed in the future
    setSelectedValues((prev) => {
      // if (attribute === 'facility_type') {
      //   // Ensure only one facility_type is selected and it cannot be unselected
      //   return {
      //     ...prev,
      //     [attribute]: [value],
      //   };
      // }

      const currentSelection = prev[attribute] || [];
      const newSelection = currentSelection.includes(value)
        ? currentSelection.filter((item) => item !== value) // Deselect if already selected
        : [...currentSelection, value]; // Select if not already selected

      return { ...prev, [attribute]: newSelection };
    });
  };

  /**
   * Renders the distinct values for a given attribute.
   * @param {*} attribute
   * @param {*} renderRow
   * @returns
   */
  const renderDistinctValues = (attribute, renderRow) => {
    const distinctValues = getDistinctValues(trailsData, attribute);

    return distinctValues.map((value, index) => renderRow(attribute, value, index));
  };

  return {
    attributes,
    expandedAttributes,
    selectedValues,
    handlePress,
    handleReset,
    handleItemPress,
    renderDistinctValues,
    setSelectedValues,
    handleFilterResults,
  };
};

export const FilterViewCommon = ({
  attributes,
  expandedAttributes,
  selectedValues,
  handlePress,
  handleItemPress,
  renderDistinctValues,
  FontAwesomeIcon,
  iconSize,
}) => {
  const AccordionComponent = ({ title, expanded, onPress, children }) => (
    <List.Accordion
      title={toTitleCase(title)}
      expanded={expanded}
      style={styles.accordion}
      onPress={onPress}
      right={() => (
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} size={iconSize} color={COLORS.border} />
      )}
      titleStyle={styles.accordionTitle}
    >
      <View style={styles.accordionContent}>{children}</View>
    </List.Accordion>
  );

  const renderRow = (attribute, value, index) => {
    const description = labelDescriptions[value];

    if (description === undefined) return null;

    const icon = descriptionIcons[value] || faHiking;
    const iconColor = difficultyStyles[value]?.filterColor || difficultyStyles[value]?.color || COLORS.primary;

    return (
      <Pressable key={index} onPress={() => handleItemPress(attribute, value)}>
        <View style={styles.row}>
          <FontAwesomeIcon icon={icon} color={iconColor} size={iconSize} style={styles.textIcon} />
          <View style={styles.textContainer}>
            <Text style={styles.textWithIcon}>{toTitleCase(value)}</Text>
            <Text style={styles.descriptionText}>{description}</Text>
          </View>
          {/* <Text style={styles.textWithIcon}>{toTitleCase(value)}</Text> */}
          {selectedValues[attribute]?.includes(value) && (
            <FontAwesomeIcon icon={faSquareCheck} color={COLORS.primary} size={iconSize} style={styles.checkIcon} />
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      {attributes.map((attribute, index) => (
        <AccordionComponent
          key={index}
          title={attribute}
          expanded={expandedAttributes[attribute]}
          onPress={() => handlePress(attribute)}
        >
          <View style={styles.accordionContent}>{renderDistinctValues(attribute, renderRow)}</View>
        </AccordionComponent>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    backgroundColor: COLORS.backgroundColor,
  },
  accordion: {
    backgroundColor: COLORS.backgroundColor,
  },
  accordionTitle: {
    color: COLORS.textDark,
    fontSize: 16,
    fontWeight: "bold",
  },
  accordionContent: {
    marginLeft: 16,
    marginTop: -8,
    backgroundColor: COLORS.backgroundColor,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 18,
    justifyContent: "space-between",
    backgroundColor: COLORS.backgroundColor,
    borderBottomColor: COLORS.border,
    borderBottomWidth: 1,
    paddingBottom: 18,
  },
  textWithIcon: {
    color: COLORS.descriptionText,
    fontSize: 16,
    flex: 1,
  },
  textIcon: {
    marginRight: 16,
  },
  checkIcon: {
    marginRight: 24,
  },
  textContainer: {
    flex: 1,
    flexDirection: "column",
  },
  descriptionText: {
    color: COLORS.border,
    fontSize: 12,
    marginTop: 4,
  },
});
